/* eslint-disable @typescript-eslint/prefer-nullish-coalescing -- verbatim legacy payload transforms: `||` fallbacks (empty contract date, phone gates) are behavior-critical and pinned by mappers.test.ts. */
import { legacyClientListSchema, type LegacyClientList } from '../model/schema';
import type { ClientsListFilter } from './filters';

/**
 * Envelope normalization + payload mappers for the legacy clients view
 * (ported from cpanel `features/clients/model/transforms.ts` — the payload
 * mappers are behavior-critical and pinned by `mappers.test.ts`: phone
 * stripping, the business_number length gate, the `is_corporate == 'yur'`
 * flag, create-vs-update avatar pick, and the diff-only vendor PATCH).
 */

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** `{data: [...], count}` envelope → typed list; anything else → empty. */
export function toLegacyClientList(raw: unknown): LegacyClientList {
  const parsed = legacyClientListSchema.safeParse(raw);
  if (parsed.success) return parsed.data;
  if (isRecord(raw) && Array.isArray(raw.data)) {
    const data = legacyClientListSchema.shape.data.safeParse(raw.data);
    return { data: data.success ? data.data : [], count: Number(raw.count) || 0 };
  }
  return { data: [], count: 0 };
}

export function toLegacyPageCount(
  list: LegacyClientList | undefined,
  limit: number,
): number {
  const total = Number(list?.count) || 0;
  if (!Number.isFinite(limit) || limit <= 0) return 0;
  return Math.ceil(total / limit);
}

export function getLegacyRowId(row: {
  id?: string | number | null | undefined;
}): string | undefined {
  return row.id != null && row.id !== '' ? String(row.id) : undefined;
}

/* ── Form payload transforms (verbatim port) ─────────────────────── */

export interface ClientFormValues {
  first_name?: string;
  last_name?: string;
  preferred_lang?: string;
  phone_number?: string;
  email?: string;
  password?: string;
  min_balance?: string | number;
  search_time?: string | number;
  restrictions?: string;
  business_number?: string;
  buisness_adress?: string;
  contract_number?: string;
  contract_start_date?: string | Date;
  buisness_desc?: string;
  business_name?: string;
  is_corporate?: string;
  is_business?: boolean;
  door_to_door_required?: boolean;
  allow_multiple_orders?: boolean;
  is_client_full_pay?: boolean;
  inn?: string;
  official_business_name?: string;
  avatar_image?: { path?: string }[];
  business_image?: { path?: string }[];
  clientType?: string;
}

const stripPhone = (phone?: string): string => `${phone?.replace(/[X() ]/g, '')}`;

const businessNumber = (value?: string): string =>
  value && value.length < 13 ? '' : (value?.replace(/[X() ]/g, '') ?? '');

/** Create: recipient payload. Avatar = FIRST uploaded image (`[0]`). */
export function mapClientCreate(data: ClientFormValues) {
  return {
    first_name: data.first_name,
    profile_photo: data?.avatar_image?.[0]?.path,
    last_name: data.last_name,
    preferred_lang: data.preferred_lang,
    phone: stripPhone(data.phone_number),
    is_client_full_pay: data.is_client_full_pay ?? false,
    search_time: Number(data?.search_time),
  };
}

/** Create: vendor payload. preferred_lang hard-coded 'ru'. */
export function mapVendorCreate(data: ClientFormValues, now: Date) {
  return {
    name: data?.first_name,
    logo: data.avatar_image?.[0]?.path,
    preferred_lang: 'ru',
    password: data?.password,
    phone: stripPhone(data.phone_number),
    email: data.email,
    minimal_balance: Number(data?.min_balance),
    enable_balance_restrictions: data?.restrictions === 'true',
    business_number: businessNumber(data?.business_number),
    address: data?.buisness_adress,
    contract_number: data?.contract_number,
    contract_start_date: data?.contract_start_date || now,
    business_desc: data?.buisness_desc,
    is_corporate: data?.is_corporate == 'yur',
    is_business: data?.is_business,
    business_logo: data?.business_image?.[(data?.business_image?.length ?? 0) - 1]?.path,
    business_name: data?.business_name,
    door_to_door_required: data?.door_to_door_required,
    is_client_full_pay: data?.is_client_full_pay ?? false,
    inn: data?.inn,
    official_business_name: data?.official_business_name,
    search_time: Number(data?.search_time),
  };
}

/** Edit: recipient payload. Avatar = LAST uploaded image. */
export function mapClientUpdate(data: ClientFormValues) {
  return {
    first_name: data.first_name,
    profile_photo: data?.avatar_image?.[(data?.avatar_image?.length ?? 0) - 1]?.path,
    last_name: data.last_name,
    preferred_lang: data.preferred_lang,
    phone: stripPhone(data.phone_number),
    is_client_full_pay: data.is_client_full_pay ?? false,
    search_time: Number(data?.search_time),
  };
}

/** Edit: vendor payload; includes allow_multiple_orders. */
export function mapVendorUpdate(data: ClientFormValues, now: Date) {
  return {
    name: data?.first_name,
    logo: data.avatar_image?.[(data?.avatar_image?.length ?? 0) - 1]?.path,
    preferred_lang: data?.preferred_lang,
    password: data?.password,
    phone: stripPhone(data.phone_number),
    email: data.email,
    minimal_balance: Number(data?.min_balance),
    enable_balance_restrictions: data?.restrictions === 'true',
    business_number: businessNumber(data?.business_number),
    address: data?.buisness_adress,
    contract_number: data?.contract_number,
    contract_start_date: data?.contract_start_date || now,
    business_desc: data?.buisness_desc,
    business_logo: data?.business_image?.[(data?.business_image?.length ?? 0) - 1]?.path,
    business_name: data?.business_name,
    is_corporate: data?.is_corporate == 'yur',
    is_business: data?.is_business,
    door_to_door_required: data?.door_to_door_required,
    allow_multiple_orders: data?.allow_multiple_orders,
    is_client_full_pay: data?.is_client_full_pay ?? false,
    inn: data?.inn,
    official_business_name: data?.official_business_name,
    search_time: Number(data?.search_time),
  };
}

function normalizeForDiff(value: unknown): unknown {
  if (value === '' || value === null || value === undefined) return null;
  if (typeof value === 'number') return Number.isNaN(value) || value === 0 ? null : value;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    if (value === 'true') return true;
    if (value === 'false') return false;
    const n = Number(value);
    if (value.trim() !== '' && !Number.isNaN(n)) return n === 0 ? null : n;
  }
  return value;
}

/** Edit-vendor diff: only genuinely changed fields are PATCHed. */
export function diffVendorFields(
  formatted: Record<string, unknown>,
  original: Record<string, unknown> | undefined,
): Record<string, unknown> {
  const hasChanged = (key: string): boolean => {
    if (key == 'password')
      return ((formatted?.[key] as string | undefined)?.length ?? 0) >= 1;
    if (key == 'contract_start_date') {
      const newValue = new Date(formatted[key] as string | Date).toISOString();
      const oldValue =
        original?.[key] == null
          ? null
          : new Date(original[key] as string | Date).toISOString();
      return newValue != oldValue;
    }
    const next = normalizeForDiff(formatted[key]);
    const prev = normalizeForDiff(original?.[key]);
    if (
      (typeof next === 'boolean' || typeof prev === 'boolean') &&
      next !== null &&
      prev !== null
    ) {
      return Boolean(next) !== Boolean(prev);
    }
    return next !== prev;
  };
  return Object.fromEntries(
    Object.keys(formatted)
      .filter((key) => hasChanged(key))
      .map((key) => [key, formatted[key]]),
  );
}

/** Export query (cpanel `getVendorExport` call-site in ClientsFeature). */
export function buildVendorExportParams(args: {
  businessType: string;
  filter: ClientsListFilter;
}): Record<string, string> {
  return {
    is_business: String(
      args.businessType === 'ALL' || args.businessType === 'NON-CORPORATE',
    ),
    is_corporate: String(
      args.businessType === 'ALL' || args.businessType === 'CORPORATE',
    ),
    order_by_order_price: 'true',
    orders_from_date: args.filter.orders_from_date,
    orders_till_date: args.filter.orders_till_date,
  };
}

/** Column visibility per segment (cpanel `columns.tsx` effectiveFilter). */
export const LEGACY_COLUMN_FIELDS = [
  'name',
  'phone_number',
  'status',
  'is_order_blocked',
  'is_sanctioned',
  'created_date',
  'balance',
  'min_balance',
  'requirements',
  'average_cheque',
  'total_order_price',
  'diff_balance',
  'orders_count',
  'sales_manager',
  'actions',
] as const;
export type LegacyColumnField = (typeof LEGACY_COLUMN_FIELDS)[number];

const EMPLOYEE_HIDDEN = new Set<string>([
  'is_order_blocked',
  'is_sanctioned',
  'average_cheque',
  'total_order_price',
  'orders_count',
  'actions',
]);
const VENDOR_ONLY = new Set<string>([
  'balance',
  'contract_number',
  'official_business_name',
  'contract_date',
  'inn',
  'diff_balance',
  'sales_manager',
  'min_balance',
]);
const REMOVED_FROM_MERCHANTS = new Set<string>([
  'created_date',
  'min_balance',
  'average_cheque',
  'total_order_price',
  'diff_balance',
  'orders_count',
  'sales_manager',
]);

export function visibleLegacyFields(clientType: string): LegacyColumnField[] {
  return LEGACY_COLUMN_FIELDS.filter((field) => {
    if (REMOVED_FROM_MERCHANTS.has(field)) return false;
    if (clientType === 'employees' && EMPLOYEE_HIDDEN.has(field)) return false;
    if (field === 'status' && !clientType.includes('vendor')) return false;
    if (VENDOR_ONLY.has(field) && !clientType.includes('vendor')) return false;
    if (field === 'requirements' && clientType !== 'employees') return false;
    return true;
  });
}
